/**
 * names — 개념 번호·라벨 uuid 를 사람의 이름으로(강의 화면 전체가 같은 이름을 쓰게 한 곳에서 만든다).
 *
 * 이름 풀이의 모양이 세 벌로 갈려 있었다(조사 2026-09-13): ConceptPicker 는 함수 (id)=>이름, LabelPicker 는 객체 {uuid:이름},
 * 해석 문장(describeTerm·describeExpr)은 { concept:fn, label:fn }. 나무 요약·빠른 편집·상세 편집·결과가 따로 만들면
 * 라벨 폴백을 빠뜨린 쪽에서 모든 라벨이 「라벨」로 찍힌다 — 그래서 여기서 한 벌을 만든다.
 *
 * 라벨 이름은 서버가 uuid → 이름을 주지 않아 이 브라우저가 기억한 것뿐이다(LabelPicker.readLabelNames).
 * 모르는 라벨은 「이름 확인 필요 · 앞 8자리」로 적는다 — 다른 라벨과 구별은 되게(설계 §W02).
 */
import { labelNameOf, labelShort } from './LabelPicker';
import { orderBounds } from './syllabusQuery';

/** 사전 → 개념 번호별 { id, name, order, type } */
export function conceptIndex(dict) {
  const m = new Map();
  (Array.isArray(dict) ? dict : []).forEach((c) => {
    const id = c?.id ?? c?.concept_id;
    if (id == null) return;
    m.set(String(id), {
      id, name: c.name ?? c.concept_name ?? null, order: c.order ?? null, type: c.concept_type ?? null, outdated: !!c.is_outdated,
    });
  });
  return m;
}

/** 모르는 라벨의 표기 — 이름 대신 쓰되 다른 라벨과 구별되게 */
export const unknownLabelText = (uuid) => `라벨 이름 확인 필요 · ${String(uuid).slice(0, 8)}`;

export { labelShort };

/**
 * 해석 문장·요약용 이름 풀이 { concept(id) → 이름|null, label(uuid) → 이름|null, labelOrUnknown(uuid) → 글자 }.
 * describeTerm 은 null 이면 번호(#170)·「라벨」로 적는다 — 요약에는 labelOrUnknown 을 쓰면 구별이 된다.
 * orderBounds() — 사전 순번의 처음·끝. 범위 유래를 「순번 100 이상」처럼 적는 데 쓴다(syllabusQuery.orderRangeText).
 */
export function makeNames(dict, labelNames = {}) {
  const idx = conceptIndex(dict);
  const bounds = orderBounds(dict);
  return {
    concept: (id) => idx.get(String(id))?.name || null,
    label: (u) => labelNameOf(labelNames, u) || unknownLabelText(u),
    conceptInfo: (id) => idx.get(String(id)) || null,
    labelKnown: (u) => !!labelNameOf(labelNames, u),
    orderBounds: () => bounds,
  };
}
